import React, { useState, useEffect } from 'react';
import {
  Settings,
  Building,
  Phone,
  Globe,
  Shield,
  CreditCard,
  Bell,
  Key,
  Volume2,
  Mic,
  Save,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff,
  User,
  Clock,
  TrendingUp,
  Palette,
  Image,
  ExternalLink
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authAPI, dashboardAPI, phoneAPI, settingsAPI, tenantAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import clsx from 'clsx';
import toast from 'react-hot-toast';

const VOICE_PREVIEW_PROFILES = {
  // Original TTS voices
  alloy: { label: 'Alloy', pitch: 1.0, rate: 1.0, hint: 'alloy', description: 'Neutral, balanced' },
  echo: { label: 'Echo', pitch: 0.9, rate: 1.05, hint: 'echo', description: 'Warm, conversational' },
  fable: { label: 'Fable', pitch: 1.1, rate: 0.95, hint: 'fable', description: 'British, narrative' },
  onyx: { label: 'Onyx', pitch: 0.8, rate: 0.9, hint: 'onyx', description: 'Deep, authoritative' },
  shimmer: { label: 'Shimmer', pitch: 1.25, rate: 1.1, hint: 'shimmer', description: 'Bright, energetic' },
  nova: { label: 'Nova', pitch: 1.05, rate: 1.0, hint: 'nova', description: 'Upbeat, feminine' },
  // New Realtime API voices
  ash: { label: 'Ash', pitch: 0.85, rate: 0.95, hint: 'ash', description: 'Masculine, confident' },
  ballad: { label: 'Ballad', pitch: 1.0, rate: 0.9, hint: 'ballad', description: 'Smooth, storytelling' },
  coral: { label: 'Coral', pitch: 1.1, rate: 1.0, hint: 'coral', description: 'Feminine, expressive' },
  sage: { label: 'Sage', pitch: 0.95, rate: 0.95, hint: 'sage', description: 'Calm, wise' },
  verse: { label: 'Verse', pitch: 1.0, rate: 1.0, hint: 'verse', description: 'Poetic, articulate' }
};

const BROWSER_VOICE_PRIORITY = {
  alloy: ['alloy', 'samantha', 'ava', 'aria', 'luna'],
  echo: ['echo', 'daniel', 'matthew', 'arthur', 'george'],
  fable: ['fable', 'emily', 'karen', 'olivia', 'serena'],
  onyx: ['onyx', 'fred', 'henry', 'albert', 'bruce'],
  shimmer: ['shimmer', 'victoria', 'allison', 'hazel', 'violet'],
  nova: ['nova', 'rachel', 'natalie', 'claire', 'scarlett'],
  // New Realtime API voices - fallback to similar browser voices
  ash: ['ash', 'james', 'david', 'michael', 'thomas'],
  ballad: ['ballad', 'alex', 'chris', 'sam', 'jamie'],
  coral: ['coral', 'sophie', 'emma', 'lily', 'maya'],
  sage: ['sage', 'oliver', 'william', 'charles', 'edward'],
  verse: ['verse', 'jordan', 'taylor', 'morgan', 'casey']
};

const VOICE_MODEL_SEQUENCE = ['alloy', 'echo', 'fable', 'onyx', 'shimmer', 'nova', 'ash', 'ballad', 'coral', 'sage', 'verse'];

const pickBrowserVoice = (voiceModel) => {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
    return null;
  }

  const voices = window.speechSynthesis.getVoices() || [];
  if (!voices.length) {
    return null;
  }

  const priorityHints = BROWSER_VOICE_PRIORITY[voiceModel] || [];
  const normalizedHints = priorityHints
    .concat(VOICE_PREVIEW_PROFILES[voiceModel]?.hint || [])
    .concat(voiceModel)
    .map((value) => value.toLowerCase())
    .filter(Boolean);

  for (const hint of normalizedHints) {
    const voice = voices.find((candidate) => candidate.name?.toLowerCase().includes(hint));
    if (voice) {
      return voice;
    }
  }

  const englishVoices = voices.filter((voice) => voice.lang?.toLowerCase().startsWith('en'));
  if (englishVoices.length) {
    const sequenceIndex = VOICE_MODEL_SEQUENCE.indexOf(voiceModel);
    if (sequenceIndex >= 0) {
      return englishVoices[sequenceIndex % englishVoices.length];
    }
    return englishVoices[0];
  }

  return voices[0];
};


const SettingsPage = () => {
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [showApiKeys, setShowApiKeys] = useState({});
  const [playingVoice, setPlayingVoice] = useState(null);
  const [previewUtterance, setPreviewUtterance] = useState(null);
  const [audioPreview, setAudioPreview] = useState(null);

  // Profile settings
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    timezone: '',
    language: 'en'
  });

  // Company settings
  const [companyData, setCompanyData] = useState({
    companyName: '',
    subdomain: '',
    industry: '',
    website: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US'
  });

  // Voice settings
  const [voiceSettings, setVoiceSettings] = useState({
    provider: 'elevenlabs',
    voice: 'alloy',
    language: 'en-US',
    speed: 1.0,
    defaultLanguage: 'en',
    voiceModel: 'alloy',
    autoDetectLanguage: true,
    enableTranscription: true,
    enableRecording: true,
    welcomeMessage: '',
    fallbackMessage: ''
  });

  // API settings
  const [apiSettings, setApiSettings] = useState({
    webhookUrl: '',
    apiKeys: {},
    rateLimits: {
      callsPerMinute: 100,
      requestsPerSecond: 10
    }
  });

  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: {
      callAlerts: true,
      systemUpdates: true,
      billingUpdates: true,
      securityAlerts: true
    },
    smsNotifications: {
      criticalAlerts: false,
      callFailures: false
    }
  });

  // AI Configuration settings
  const [aiConfig, setAiConfig] = useState({
    tone: 'professional',
    customInstructions: '',
    assistantRole: '',
    callerIdentity: '',
    additionalContext: '',
    temperature: 0.7,
    maxTokens: 150
  });
  const [savingAI, setSavingAI] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Phone numbers
  const [phoneNumbers, setPhoneNumbers] = useState([]);

  // Branding settings
  const [brandingData, setBrandingData] = useState({
    brandName: '',
    brandLogoUrl: '',
    brandFaviconUrl: '',
    brandPrimaryColor: '#000000',
    brandSecondaryColor: '#ffffff',
    brandAccentColor: '#6366f1',
    customDomain: '',
    hideAlfredBranding: false,
    customEmailFromName: '',
    customEmailFromAddress: ''
  });
  const [savingBranding, setSavingBranding] = useState(false);
  const [loadingBranding, setLoadingBranding] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.getVoices();
      const handleVoicesChanged = () => {
        window.speechSynthesis.getVoices();
      };

      window.speechSynthesis.addEventListener('voiceschanged', handleVoicesChanged);

      return () => {
        window.speechSynthesis.removeEventListener('voiceschanged', handleVoicesChanged);
      };
    }
  }, []);

  useEffect(() => () => {
    if (audioPreview) {
      audioPreview.pause();
      if (audioPreview.src) {
        URL.revokeObjectURL(audioPreview.src);
      }
    }

    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  }, [audioPreview]);

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        timezone: user.timezone || 'America/Chicago', // Default to Central Time
        language: user.language || 'en'
      });

      if (user.tenant) {
        setCompanyData({
          companyName: user.tenant.companyName || '',
          subdomain: user.tenant.subdomain || '',
          industry: user.tenant.industry || '',
          website: user.tenant.website || '',
          address: user.tenant.address || '',
          city: user.tenant.city || '',
          state: user.tenant.state || '',
          zipCode: user.tenant.zipCode || '',
          country: user.tenant.country || 'US'
        });
      }
    }

    loadSettings();
    loadAIConfig();
    loadBranding();
  }, [user]);

  const loadSettings = async () => {
    try {
      // Wrap voice settings to prevent error
      try {
        const voiceResponse = await dashboardAPI.getVoiceSettings();
        const backendVoiceSettings = voiceResponse.data.data || {};
        const savedVoiceModel = backendVoiceSettings.voiceModel || backendVoiceSettings.voice || 'alloy';
        const savedVoice = backendVoiceSettings.voice && backendVoiceSettings.voice !== 'default'
          ? backendVoiceSettings.voice
          : savedVoiceModel;
        const savedSpeed = backendVoiceSettings.speed || backendVoiceSettings.speechSpeed || 1.0;

        const mappedVoiceSettings = {
          provider: backendVoiceSettings.provider || 'elevenlabs',
          voice: savedVoice,
          language: backendVoiceSettings.language || backendVoiceSettings.defaultLanguage || 'en-US',
          speed: savedSpeed,
          defaultLanguage: backendVoiceSettings.defaultLanguage || 'en',
          voiceModel: savedVoiceModel,
          autoDetectLanguage: backendVoiceSettings.autoDetectLanguage ?? true,
          enableTranscription: backendVoiceSettings.enableTranscription ?? true,
          enableRecording: backendVoiceSettings.enableRecording ?? true,
          welcomeMessage: backendVoiceSettings.welcomeMessage || '',
          fallbackMessage: backendVoiceSettings.fallbackMessage || ''
        };
        setVoiceSettings(mappedVoiceSettings);
      } catch (voiceError) {
        // Silently use defaults for voice settings
        console.log('Voice settings not available, using defaults');
        setVoiceSettings({
          provider: 'elevenlabs',
          voice: 'alloy',
          language: 'en-US',
          speed: 1.0,
          defaultLanguage: 'en',
          voiceModel: 'alloy',
          autoDetectLanguage: true,
          enableTranscription: true,
          enableRecording: true,
          welcomeMessage: '',
          fallbackMessage: ''
        });
      }

      // Load other settings
      const [apiRes, notificationRes, phoneRes] = await Promise.all([
        dashboardAPI.getApiSettings().catch(() => ({ data: { data: {} } })),
        dashboardAPI.getNotificationSettings().catch(() => ({ data: { data: {} } })),
        phoneAPI.getMyNumbers().catch(() => ({ data: { data: [] } }))
      ]);

      setApiSettings(apiRes.data.data || {});
      setNotificationSettings(notificationRes.data.data || {});

      const numbers = phoneRes.data?.data || phoneRes.data || [];
      console.log('Loaded phone numbers:', numbers);
      setPhoneNumbers(numbers);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const loadAIConfig = async () => {
    try {
      const response = await settingsAPI.getAIConfig();
      if (response.data?.data) {
        setAiConfig({
          tone: response.data.data.tone || 'professional',
          customInstructions: response.data.data.customInstructions || '',
          assistantRole: response.data.data.assistantRole || '',
          callerIdentity: response.data.data.callerIdentity || '',
          additionalContext: response.data.data.additionalContext || '',
          temperature: response.data.data.temperature || 0.7,
          maxTokens: response.data.data.maxTokens || 150
        });
      }
    } catch (error) {
      console.error('Error loading AI config:', error);
      // Silently use defaults
    }
  };

  const loadBranding = async () => {
    setLoadingBranding(true);
    try {
      const response = await tenantAPI.getBranding();
      if (response.data?.branding) {
        const branding = response.data.branding;
        setBrandingData({
          brandName: branding.brandName || '',
          brandLogoUrl: branding.logoUrl || '',
          brandFaviconUrl: branding.faviconUrl || '',
          brandPrimaryColor: branding.colors?.primary || '#000000',
          brandSecondaryColor: branding.colors?.secondary || '#ffffff',
          brandAccentColor: branding.colors?.accent || '#6366f1',
          customDomain: branding.customDomain || '',
          hideAlfredBranding: branding.hideAlfredBranding || false,
          customEmailFromName: branding.email?.fromName || '',
          customEmailFromAddress: branding.email?.fromAddress || ''
        });
      }
    } catch (error) {
      console.error('Error loading branding:', error);
    } finally {
      setLoadingBranding(false);
    }
  };

  const handleBrandingSubmit = async (e) => {
    e.preventDefault();
    setSavingBranding(true);
    try {
      await tenantAPI.updateBranding(brandingData);
      toast.success('Branding settings saved successfully');
    } catch (error) {
      console.error('Error saving branding:', error);
      toast.error(error.response?.data?.message || 'Failed to save branding settings');
    } finally {
      setSavingBranding(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Debug: Log what we're sending
      console.log('Sending profile update:', profileData);
      console.log('Data type:', typeof profileData);
      console.log('Keys:', Object.keys(profileData));

      // Make sure we're sending the right structure
      // Always send all fields to ensure they're saved properly
      const cleanedData = {
        name: profileData.name?.trim() || null,
        phone: profileData.phone?.trim() || null,
        timezone: profileData.timezone || 'America/Chicago',
        language: profileData.language || 'en'
      };

      console.log('Cleaned data:', cleanedData);

      const response = await authAPI.updateProfile(cleanedData);

      console.log('Profile update response:', response);

      if (response.data.success) {
        updateUser(response.data.profile);
        toast.success('Profile updated successfully');
      }
    } catch (error) {
      console.error('Profile update error:', error.response);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await dashboardAPI.updateTenantSettings(companyData);
      toast.success('Company settings updated successfully');
    } catch (error) {
      toast.error('Failed to update company settings');
    } finally {
      setLoading(false);
    }
  };

  const handleVoiceSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const normalizedSpeed = Number.isFinite(Number(voiceSettings.speed))
        ? Number(voiceSettings.speed)
        : 1.0;

      const voiceModel = voiceSettings.voiceModel || voiceSettings.voice || 'alloy';

      const payload = {
        provider: voiceSettings.provider || 'elevenlabs',
        voice: voiceModel,
        language: voiceSettings.language || voiceSettings.defaultLanguage || 'en-US',
        speed: normalizedSpeed,
        defaultLanguage: voiceSettings.defaultLanguage || 'en',
        voiceModel,
        autoDetectLanguage: voiceSettings.autoDetectLanguage,
        enableTranscription: voiceSettings.enableTranscription,
        enableRecording: voiceSettings.enableRecording,
        welcomeMessage: voiceSettings.welcomeMessage,
        fallbackMessage: voiceSettings.fallbackMessage
      };

      const response = await dashboardAPI.updateVoiceSettings(payload);
      const savedSettings = response.data?.data || payload;

      setVoiceSettings(prev => ({
        ...prev,
        ...savedSettings,
        provider: savedSettings.provider || payload.provider,
        voice: savedSettings.voice && savedSettings.voice !== 'default'
          ? savedSettings.voice
          : savedSettings.voiceModel || payload.voice,
        language: savedSettings.language || payload.language,
        speed: savedSettings.speed || savedSettings.speechSpeed || payload.speed,
        voiceModel: savedSettings.voiceModel || savedSettings.voice || payload.voiceModel,
        welcomeMessage: savedSettings.welcomeMessage || payload.welcomeMessage,
        fallbackMessage: savedSettings.fallbackMessage || payload.fallbackMessage,
        enableRecording: savedSettings.enableRecording ?? payload.enableRecording,
        enableTranscription: savedSettings.enableTranscription ?? payload.enableTranscription,
        autoDetectLanguage: savedSettings.autoDetectLanguage ?? payload.autoDetectLanguage
      }));

      toast.success('Voice settings updated successfully');
    } catch (error) {
      console.error('Voice settings error:', error);
      toast.error(error.response?.data?.error || 'Failed to update voice settings');
    } finally {
      setLoading(false);
    }
  };

  const handleApiSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await dashboardAPI.updateApiSettings(apiSettings);
      toast.success('API settings updated successfully');
    } catch (error) {
      toast.error('Failed to update API settings');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await dashboardAPI.updateNotificationSettings(notificationSettings);
      toast.success('Notification settings updated successfully');
    } catch (error) {
      toast.error('Failed to update notification settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAIConfig = async () => {
    try {
      setSavingAI(true);
      await settingsAPI.updateAIConfig(aiConfig);
      toast.success('AI configuration saved successfully');
    } catch (error) {
      console.error('Error saving AI config:', error);
      toast.error('Failed to save AI configuration');
    } finally {
      setSavingAI(false);
    }
  };

  const generateApiKey = async (keyType) => {
    try {
      const response = await dashboardAPI.generateApiKey(keyType);
      setApiSettings(prev => ({
        ...prev,
        apiKeys: {
          ...prev.apiKeys,
          [keyType]: response.data.data.key
        }
      }));
      toast.success(`${keyType} API key generated successfully`);
    } catch (error) {
      toast.error('Failed to generate API key');
    }
  };

  const toggleApiKeyVisibility = (keyType) => {
    setShowApiKeys(prev => ({
      ...prev,
      [keyType]: !prev[keyType]
    }));
  };

  const playVoicePreview = async (voiceModel) => {
    if (typeof window === 'undefined') {
      toast.error('Voice previews are not available in this environment');
      return;
    }

    // Stop any playback already in progress
    if (audioPreview) {
      audioPreview.pause();
      if (audioPreview.src) {
        URL.revokeObjectURL(audioPreview.src);
      }
      setAudioPreview(null);
    }

    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }

    if (previewUtterance) {
      setPreviewUtterance(null);
    }

    setPlayingVoice(voiceModel);

    const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
    const apiBase = import.meta.env.VITE_API_BASE_URL || '/api';
    const profile = VOICE_PREVIEW_PROFILES[voiceModel] || VOICE_PREVIEW_PROFILES.alloy;

    try {
      if (!token) {
        throw new Error('AUTH_REQUIRED');
      }

      const response = await fetch(`${apiBase}/settings/voice/preview/${voiceModel}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 501) {
          toast('Cloud voice preview is not configured. Using browser voice instead.');
        } else if (response.status === 502) {
          toast('The voice provider rejected the request. Using browser voice instead.', { icon: '⚠️' });
        }
        throw new Error(`VOICE_PREVIEW_HTTP_${response.status}`);
      }

      const audioBlob = await response.blob();
      const objectUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(objectUrl);

      audio.onended = () => {
        setPlayingVoice(null);
        URL.revokeObjectURL(objectUrl);
        setAudioPreview(null);
      };

      audio.onerror = (event) => {
        console.error('Voice preview audio error:', event.message || event.type);
        toast.error('Voice preview not available right now');
        setPlayingVoice(null);
        URL.revokeObjectURL(objectUrl);
        setAudioPreview(null);
      };

      setAudioPreview(audio);
      await audio.play();
      toast.success(`Playing ${profile.label} preview`);
      return;
    } catch (error) {
      if (error && error.message === 'AUTH_REQUIRED') {
        toast.error('Please sign in again to preview voices.');
        setPlayingVoice(null);
        return;
      }

      if (error && /^VOICE_PREVIEW_HTTP_/.test(error.message)) {
        // HTTP status already handled above; fall back to browser voice.
      }

      console.warn('Falling back to browser voice preview:', error);
    }

    if (!('speechSynthesis' in window)) {
      toast.error('Voice preview not available right now');
      setPlayingVoice(null);
      return;
    }

    try {
      const utterance = new SpeechSynthesisUtterance('Thank you for choosing this voice.');

      const browserVoice = pickBrowserVoice(voiceModel);
      if (browserVoice) {
        utterance.voice = browserVoice;
        utterance.lang = browserVoice.lang || voiceSettings.language || 'en-US';
      } else {
        utterance.lang = voiceSettings.language || 'en-US';
      }
      utterance.pitch = profile.pitch;
      const speedMultiplier = Math.min(2, Math.max(0.5, voiceSettings.speed || 1.0));
      utterance.rate = Math.min(2, Math.max(0.5, profile.rate * speedMultiplier));

      setPreviewUtterance(utterance);

      utterance.onend = () => {
        setPlayingVoice(null);
        setPreviewUtterance(null);
      };

      utterance.onerror = (event) => {
        console.error('Speech preview error:', event.error);
        toast.error('Voice preview not available right now');
        setPlayingVoice(null);
        setPreviewUtterance(null);
      };

      window.speechSynthesis.speak(utterance);
      toast.success(`Playing ${profile.label} preview`);
    } catch (error) {
      console.error('Error playing fallback voice preview:', error);
      toast.error('Voice preview not available right now');
      setPlayingVoice(null);
      setPreviewUtterance(null);
    }
  };

  const tabs = [
    { id: 'profile', label: 'User Profile', icon: User },
    { id: 'branding', label: 'Branding', icon: Palette },
    { id: 'usage', label: 'Usage & Billing', icon: Clock },
    { id: 'voice', label: 'Voice Settings', icon: Mic },
    { id: 'ai', label: 'AI Configuration', icon: Settings },
    { id: 'phone', label: 'Phone Numbers', icon: Phone },
    { id: 'api', label: 'API & Webhooks', icon: Key },
    { id: 'notifications', label: 'Notifications', icon: Bell }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">
          Manage your account, company, and platform settings
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar Navigation */}
        <div className="lg:w-64 flex-shrink-0">
          <nav className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={clsx(
                      'w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                      activeTab === tab.id
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    )}
                  >
                    <Icon className="w-4 h-4 mr-3" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </nav>
        </div>

        {/* Content Area */}
        <div className="flex-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* User Profile */}
            {activeTab === 'profile' && (
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">User Profile</h3>

                <form onSubmit={handleProfileSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={profileData.email}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Contact support to change your email address
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        value={profileData.name}
                        onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter your name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={profileData.phone}
                        onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Timezone
                      </label>
                      <select
                        value={profileData.timezone}
                        onChange={(e) => setProfileData(prev => ({ ...prev, timezone: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="America/New_York">Eastern Time (ET)</option>
                        <option value="America/Chicago">Central Time (CT)</option>
                        <option value="America/Denver">Mountain Time (MT)</option>
                        <option value="America/Los_Angeles">Pacific Time (PT)</option>
                        <option value="America/Anchorage">Alaska Time (AKT)</option>
                        <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
                        <option value="UTC">UTC</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Language
                      </label>
                      <select
                        value={profileData.language}
                        onChange={(e) => setProfileData(prev => ({ ...prev, language: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="en">English</option>
                        <option value="es">Spanish</option>
                        <option value="fr">French</option>
                        <option value="de">German</option>
                        <option value="pt">Portuguese</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-end pt-4 border-t border-gray-200">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {loading ? (
                        <LoadingSpinner size="sm" color="white" className="mr-2" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save Profile
                    </button>
                  </div>
                </form>

                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-4">Account Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Role:</span>
                      <span className="ml-2 font-medium text-gray-900 capitalize">{user?.role || 'User'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Company:</span>
                      <span className="ml-2 font-medium text-gray-900">{user?.tenant?.companyName || 'Not set'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Account Status:</span>
                      <span className="ml-2 font-medium text-green-600">Active</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Email Verified:</span>
                      <span className={clsx(
                        'ml-2 font-medium',
                        user?.emailVerified ? 'text-green-600' : 'text-yellow-600'
                      )}>
                        {user?.emailVerified ? 'Yes' : 'Pending'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Branding */}
            {activeTab === 'branding' && (
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Branding & White Label</h3>
                <p className="text-gray-600 mb-6">
                  Customize the appearance of your Alfred Voice instance with your brand colors and logo.
                </p>

                {loadingBranding ? (
                  <div className="flex items-center justify-center py-12">
                    <LoadingSpinner />
                  </div>
                ) : (
                  <form onSubmit={handleBrandingSubmit} className="space-y-8">
                    {/* Brand Identity */}
                    <div className="border-b border-gray-200 pb-6">
                      <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                        <Image className="w-5 h-5 mr-2" />
                        Brand Identity
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Brand Name
                          </label>
                          <input
                            type="text"
                            value={brandingData.brandName}
                            onChange={(e) => setBrandingData({ ...brandingData, brandName: e.target.value })}
                            placeholder={user?.tenant?.companyName || 'Your brand name'}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Displayed in headers and emails. Leave blank to use company name.
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Logo URL
                          </label>
                          <input
                            type="url"
                            value={brandingData.brandLogoUrl}
                            onChange={(e) => setBrandingData({ ...brandingData, brandLogoUrl: e.target.value })}
                            placeholder="https://example.com/logo.png"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Recommended size: 200x50 pixels (PNG or SVG)
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Favicon URL
                          </label>
                          <input
                            type="url"
                            value={brandingData.brandFaviconUrl}
                            onChange={(e) => setBrandingData({ ...brandingData, brandFaviconUrl: e.target.value })}
                            placeholder="https://example.com/favicon.ico"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Custom Domain
                          </label>
                          <input
                            type="text"
                            value={brandingData.customDomain}
                            onChange={(e) => setBrandingData({ ...brandingData, customDomain: e.target.value })}
                            placeholder="app.yourdomain.com"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Contact support to configure DNS for your custom domain
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Brand Colors - Alfred Style (Black & White) */}
                    <div className="border-b border-gray-200 pb-6">
                      <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                        <Palette className="w-5 h-5 mr-2" />
                        Brand Colors
                      </h4>
                      <p className="text-sm text-gray-600 mb-4">
                        Alfred uses a clean black and white aesthetic. Customize your accent color for highlights.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Primary Color
                          </label>
                          <div className="flex items-center gap-3">
                            <input
                              type="color"
                              value={brandingData.brandPrimaryColor}
                              onChange={(e) => setBrandingData({ ...brandingData, brandPrimaryColor: e.target.value })}
                              className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                            />
                            <input
                              type="text"
                              value={brandingData.brandPrimaryColor}
                              onChange={(e) => setBrandingData({ ...brandingData, brandPrimaryColor: e.target.value })}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent font-mono text-sm"
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Default: #000000 (Black)</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Secondary Color
                          </label>
                          <div className="flex items-center gap-3">
                            <input
                              type="color"
                              value={brandingData.brandSecondaryColor}
                              onChange={(e) => setBrandingData({ ...brandingData, brandSecondaryColor: e.target.value })}
                              className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                            />
                            <input
                              type="text"
                              value={brandingData.brandSecondaryColor}
                              onChange={(e) => setBrandingData({ ...brandingData, brandSecondaryColor: e.target.value })}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent font-mono text-sm"
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Default: #ffffff (White)</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Accent Color
                          </label>
                          <div className="flex items-center gap-3">
                            <input
                              type="color"
                              value={brandingData.brandAccentColor}
                              onChange={(e) => setBrandingData({ ...brandingData, brandAccentColor: e.target.value })}
                              className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                            />
                            <input
                              type="text"
                              value={brandingData.brandAccentColor}
                              onChange={(e) => setBrandingData({ ...brandingData, brandAccentColor: e.target.value })}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent font-mono text-sm"
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">For buttons and highlights</p>
                        </div>
                      </div>

                      {/* Color Preview */}
                      <div className="mt-6 p-4 rounded-lg border border-gray-200">
                        <p className="text-sm font-medium text-gray-700 mb-3">Preview</p>
                        <div className="flex items-center gap-4">
                          <div
                            className="w-24 h-12 rounded-lg flex items-center justify-center text-sm font-medium"
                            style={{
                              backgroundColor: brandingData.brandPrimaryColor,
                              color: brandingData.brandSecondaryColor
                            }}
                          >
                            Primary
                          </div>
                          <div
                            className="w-24 h-12 rounded-lg flex items-center justify-center text-sm font-medium border"
                            style={{
                              backgroundColor: brandingData.brandSecondaryColor,
                              color: brandingData.brandPrimaryColor,
                              borderColor: brandingData.brandPrimaryColor
                            }}
                          >
                            Secondary
                          </div>
                          <div
                            className="w-24 h-12 rounded-lg flex items-center justify-center text-sm font-medium text-white"
                            style={{
                              backgroundColor: brandingData.brandAccentColor
                            }}
                          >
                            Accent
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Email Branding */}
                    <div className="border-b border-gray-200 pb-6">
                      <h4 className="text-md font-medium text-gray-900 mb-4">Email Branding</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email From Name
                          </label>
                          <input
                            type="text"
                            value={brandingData.customEmailFromName}
                            onChange={(e) => setBrandingData({ ...brandingData, customEmailFromName: e.target.value })}
                            placeholder="Your Company"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email From Address
                          </label>
                          <input
                            type="email"
                            value={brandingData.customEmailFromAddress}
                            onChange={(e) => setBrandingData({ ...brandingData, customEmailFromAddress: e.target.value })}
                            placeholder="noreply@yourdomain.com"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Requires domain verification. Contact support to set up.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* White Label Options */}
                    <div className="pb-6">
                      <h4 className="text-md font-medium text-gray-900 mb-4">White Label Options</h4>
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">Hide Alfred Branding</p>
                          <p className="text-sm text-gray-600">
                            Remove "Powered by Alfred Voice" from the interface
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={brandingData.hideAlfredBranding}
                            onChange={(e) => setBrandingData({ ...brandingData, hideAlfredBranding: e.target.checked })}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
                        </label>
                      </div>
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={savingBranding}
                        className="flex items-center px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
                      >
                        {savingBranding ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Save Branding Settings
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}

            {/* Usage & Billing */}
            {activeTab === 'usage' && (
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Usage & Billing</h3>

                {/* Current Plan */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-6 border border-blue-100">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-600">Current Plan</h4>
                      <p className="text-2xl font-bold text-gray-900 capitalize">
                        {user?.tenant?.plan || 'Starter'}
                      </p>
                    </div>
                    <div className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                      Active
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Max Users</p>
                      <p className="font-semibold text-gray-900">{user?.tenant?.features?.maxUsers || 5}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Monthly Minutes</p>
                      <p className="font-semibold text-gray-900">{user?.tenant?.resourceLimits?.monthlyMinutes?.toLocaleString() || '1,000'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Concurrent Calls</p>
                      <p className="font-semibold text-gray-900">{user?.tenant?.resourceLimits?.concurrentCalls || 10}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Storage</p>
                      <p className="font-semibold text-gray-900">{user?.tenant?.resourceLimits?.storageGB || 10} GB</p>
                    </div>
                  </div>
                </div>

                {/* Minutes Usage */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-md font-semibold text-gray-900 flex items-center">
                      <Clock className="w-5 h-5 mr-2 text-blue-600" />
                      Call Minutes Usage
                    </h4>
                    <span className="text-sm text-gray-500">This billing period</span>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Minutes Used</span>
                      <span className="font-medium text-gray-900">
                        {(user?.tenant?.minutesUsed || 0).toLocaleString()} / {(user?.tenant?.resourceLimits?.monthlyMinutes || 1000).toLocaleString()}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className={clsx(
                          'h-3 rounded-full transition-all duration-500',
                          ((user?.tenant?.minutesUsed || 0) / (user?.tenant?.resourceLimits?.monthlyMinutes || 1000)) > 0.9
                            ? 'bg-red-500'
                            : ((user?.tenant?.minutesUsed || 0) / (user?.tenant?.resourceLimits?.monthlyMinutes || 1000)) > 0.7
                            ? 'bg-yellow-500'
                            : 'bg-blue-600'
                        )}
                        style={{
                          width: `${Math.min(100, ((user?.tenant?.minutesUsed || 0) / (user?.tenant?.resourceLimits?.monthlyMinutes || 1000)) * 100)}%`
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {((user?.tenant?.resourceLimits?.monthlyMinutes || 1000) - (user?.tenant?.minutesUsed || 0)).toLocaleString()} minutes remaining
                    </p>
                  </div>

                  {/* Usage Stats */}
                  <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900">{user?.tenant?.totalCalls || 0}</p>
                      <p className="text-xs text-gray-500">Total Calls</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900">{Math.round((user?.tenant?.minutesUsed || 0) / Math.max(1, user?.tenant?.totalCalls || 1))}m</p>
                      <p className="text-xs text-gray-500">Avg. Duration</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">
                        {Math.round(((user?.tenant?.minutesUsed || 0) / (user?.tenant?.resourceLimits?.monthlyMinutes || 1000)) * 100)}%
                      </p>
                      <p className="text-xs text-gray-500">Usage</p>
                    </div>
                  </div>
                </div>

                {/* Upgrade CTA */}
                {(user?.tenant?.plan === 'starter' || user?.tenant?.plan === 'professional') && (
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-lg mb-1">Need more minutes?</h4>
                        <p className="text-indigo-100 text-sm">
                          Upgrade your plan for more call minutes and advanced features.
                        </p>
                      </div>
                      <button className="bg-white text-indigo-600 px-4 py-2 rounded-lg font-medium hover:bg-indigo-50 transition-colors">
                        Upgrade Plan
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Voice Settings */}
            {activeTab === 'voice' && (
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Voice Configuration</h3>

                <form onSubmit={handleVoiceSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Voice Model
                      </label>
                      <div className="flex gap-2">
                        <select
                          value={voiceSettings.voiceModel}
                          onChange={(e) => {
                            const selectedVoice = e.target.value;
                            setVoiceSettings(prev => ({
                              ...prev,
                              voiceModel: selectedVoice,
                              voice: selectedVoice
                            }));
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <optgroup label="Standard Voices">
                            <option value="alloy">Alloy - Neutral, balanced</option>
                            <option value="echo">Echo - Warm, conversational</option>
                            <option value="fable">Fable - British, narrative</option>
                            <option value="onyx">Onyx - Deep, authoritative</option>
                            <option value="nova">Nova - Upbeat, feminine</option>
                            <option value="shimmer">Shimmer - Bright, energetic</option>
                          </optgroup>
                          <optgroup label="Realtime API Voices">
                            <option value="ash">Ash - Masculine, confident</option>
                            <option value="ballad">Ballad - Smooth, storytelling</option>
                            <option value="coral">Coral - Feminine, expressive</option>
                            <option value="sage">Sage - Calm, wise</option>
                            <option value="verse">Verse - Poetic, articulate</option>
                          </optgroup>
                        </select>
                        <button
                          type="button"
                          onClick={() => playVoicePreview(voiceSettings.voiceModel)}
                          disabled={playingVoice === voiceSettings.voiceModel}
                          className={clsx(
                            'flex items-center px-3 py-2 rounded-lg transition-colors',
                            playingVoice === voiceSettings.voiceModel
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          )}
                          title={playingVoice === voiceSettings.voiceModel ? 'Playing preview...' : 'Preview voice'}
                        >
                          <Volume2 className={clsx(
                            'w-4 h-4',
                            playingVoice === voiceSettings.voiceModel && 'animate-pulse'
                          )} />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Click the speaker icon to preview this voice
                      </p>
                    </div>

                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="autoDetectLanguage"
                        checked={voiceSettings.autoDetectLanguage}
                        onChange={(e) => setVoiceSettings(prev => ({ ...prev, autoDetectLanguage: e.target.checked }))}
                        className="mr-3"
                      />
                      <label htmlFor="autoDetectLanguage" className="text-sm text-gray-700">
                        Auto-detect caller language
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="enableTranscription"
                        checked={voiceSettings.enableTranscription}
                        onChange={(e) => setVoiceSettings(prev => ({ ...prev, enableTranscription: e.target.checked }))}
                        className="mr-3"
                      />
                      <label htmlFor="enableTranscription" className="text-sm text-gray-700">
                        Enable call transcription
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="enableRecording"
                        checked={voiceSettings.enableRecording}
                        onChange={(e) => setVoiceSettings(prev => ({ ...prev, enableRecording: e.target.checked }))}
                        className="mr-3"
                      />
                      <label htmlFor="enableRecording" className="text-sm text-gray-700">
                        Enable call recording
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Welcome Message
                    </label>
                    <textarea
                      value={voiceSettings.welcomeMessage}
                      onChange={(e) => setVoiceSettings(prev => ({ ...prev, welcomeMessage: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter the welcome message for callers..."
                    />
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {loading ? (
                        <LoadingSpinner size="sm" color="white" className="mr-2" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save Voice Settings
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* AI Configuration */}
            {activeTab === 'ai' && (
              <div className="p-6">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">AI Configuration</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Customize your AI assistant&apos;s behavior and personality
                  </p>
                </div>

                <div className="space-y-6">
                  {/* Tone Selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tone
                    </label>
                    <select
                      value={aiConfig.tone}
                      onChange={(e) => setAiConfig({...aiConfig, tone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="professional">Professional</option>
                      <option value="casual">Casual</option>
                      <option value="friendly">Friendly</option>
                      <option value="formal">Formal</option>
                      <option value="enthusiastic">Enthusiastic</option>
                      <option value="empathetic">Empathetic</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Set the overall tone and style of AI responses
                    </p>
                  </div>

                  {/* Assistant Role */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assistant Role (Optional)
                    </label>
                    <input
                      type="text"
                      value={aiConfig.assistantRole}
                      onChange={(e) => setAiConfig({...aiConfig, assistantRole: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Executive Assistant to Brian Southers"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Define who the AI is. Leave blank for default.
                    </p>
                  </div>

                  {/* Caller Identity for Outbound Calls */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Outbound Caller Identity (Optional)
                    </label>
                    <input
                      type="text"
                      value={aiConfig.callerIdentity}
                      onChange={(e) => setAiConfig({...aiConfig, callerIdentity: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Sam from RelayWorks, your AI assistant from RelayWorks"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      How the AI introduces itself on outbound calls (Sales Pipeline, QA). Leave blank for "a representative from [Company Name]".
                    </p>
                  </div>

                  {/* Custom Instructions */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Custom Instructions
                    </label>
                    <textarea
                      value={aiConfig.customInstructions}
                      onChange={(e) => setAiConfig({...aiConfig, customInstructions: e.target.value})}
                      rows={8}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                      placeholder="Example: You are the executive Assistant to Brian Southers. Take messages for Brian. If callers ask about Mule Works provide them with the basic information that Mule Works focuses on building large scale UAV to handle up to 1,000 pounds for 200 miles. Additional information can be found on the website at muleworks.co. If callers ask about Relay Work explain the two SaaS products AlfredTAV and AlfredCC. Always get their contact information which will include the phone number, name, and email."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Provide specific instructions for how the AI should behave
                    </p>
                  </div>

                  {/* Additional Context */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Additional Context (Optional)
                    </label>
                    <textarea
                      value={aiConfig.additionalContext}
                      onChange={(e) => setAiConfig({...aiConfig, additionalContext: e.target.value})}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Additional information about your company, products, or services"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Extra context to help the AI answer questions
                    </p>
                  </div>

                  {/* Advanced Settings - Collapsible */}
                  <div className="border-t border-gray-200 pt-6">
                    <button
                      type="button"
                      onClick={() => setShowAdvanced(!showAdvanced)}
                      className="flex items-center justify-between w-full text-left"
                    >
                      <h4 className="text-sm font-medium text-gray-900">Advanced Settings</h4>
                      <svg
                        className={clsx(
                          'w-5 h-5 text-gray-500 transition-transform',
                          showAdvanced && 'rotate-180'
                        )}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {showAdvanced && (
                      <div className="mt-4 space-y-6">
                        {/* Temperature Slider */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                              Response Creativity
                            </label>
                            <span className="text-sm text-gray-600 font-mono">
                              {aiConfig.temperature.toFixed(1)}
                            </span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="2"
                            step="0.1"
                            value={aiConfig.temperature}
                            onChange={(e) => setAiConfig({...aiConfig, temperature: parseFloat(e.target.value)})}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                          />
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>More Focused (0.0)</span>
                            <span>More Creative (2.0)</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Lower values = more focused and deterministic. Higher values = more creative and varied.
                          </p>
                        </div>

                        {/* Max Tokens Slider */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                              Response Length
                            </label>
                            <span className="text-sm text-gray-600 font-mono">
                              {aiConfig.maxTokens} tokens
                            </span>
                          </div>
                          <input
                            type="range"
                            min="50"
                            max="500"
                            step="10"
                            value={aiConfig.maxTokens}
                            onChange={(e) => setAiConfig({...aiConfig, maxTokens: parseInt(e.target.value)})}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                          />
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>Shorter (50)</span>
                            <span>Longer (500)</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Maximum length of AI responses. Lower values for brief responses, higher for detailed ones.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={handleSaveAIConfig}
                      disabled={savingAI}
                      className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {savingAI ? (
                        <LoadingSpinner size="sm" color="white" className="mr-2" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      {savingAI ? 'Saving...' : 'Save AI Configuration'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Phone Numbers */}
            {activeTab === 'phone' && (
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Phone Numbers</h3>

                <div className="space-y-4">
                  {phoneNumbers && phoneNumbers.length > 0 ? (
                    <div className="space-y-3">
                      {phoneNumbers.map((number, index) => (
                        <div
                          key={number.id || index}
                          className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50"
                        >
                          <div className="flex items-center space-x-3">
                            <Phone className="w-5 h-5 text-blue-600" />
                            <div>
                              <p className="font-medium text-gray-900">
                                {number.phoneNumber || number.number}
                              </p>
                              <p className="text-sm text-gray-500">
                                {number.carrier || 'Carrier'} • {number.countryCode || 'US'} • {number.status || 'active'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {number.status === 'active' && (
                              <span className="flex items-center text-sm text-green-600">
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Active
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                      <Phone className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600 font-medium mb-1">No Phone Numbers</p>
                      <p className="text-sm text-gray-500">
                        You haven&apos;t provisioned any phone numbers yet.
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        Go to <span className="font-medium">Business Profile → Phone Numbers</span> to get started.
                      </p>
                    </div>
                  )}
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">About Phone Numbers</h4>
                  <p className="text-sm text-gray-600">
                    Your Vonage/Twilio phone numbers are used for inbound and outbound calls.
                    Each number can handle multiple concurrent calls based on your plan.
                  </p>
                </div>
              </div>
            )}

            {/* API Settings */}
            {activeTab === 'api' && (
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">API & Webhook Configuration</h3>

                <form onSubmit={handleApiSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Webhook URL
                    </label>
                    <input
                      type="url"
                      value={apiSettings.webhookUrl}
                      onChange={(e) => setApiSettings(prev => ({ ...prev, webhookUrl: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://your-server.com/webhook"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Receive real-time notifications about calls and events
                    </p>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900">API Keys</h4>

                    {['production', 'staging'].map((keyType) => (
                      <div key={keyType} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <label className="text-sm font-medium text-gray-700 capitalize">
                            {keyType} API Key
                          </label>
                          <button
                            type="button"
                            onClick={() => generateApiKey(keyType)}
                            className="text-sm text-blue-600 hover:text-blue-700"
                          >
                            Generate New
                          </button>
                        </div>

                        <div className="flex items-center space-x-2">
                          <input
                            type={showApiKeys[keyType] ? 'text' : 'password'}
                            value={apiSettings.apiKeys[keyType] || ''}
                            readOnly
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                            placeholder={`No ${keyType} API key generated`}
                          />
                          <button
                            type="button"
                            onClick={() => toggleApiKeyVisibility(keyType)}
                            className="p-2 text-gray-400 hover:text-gray-600"
                          >
                            {showApiKeys[keyType] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {loading ? (
                        <LoadingSpinner size="sm" color="white" className="mr-2" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save API Settings
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Notification Settings */}
            {activeTab === 'notifications' && (
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Notification Preferences</h3>

                <form onSubmit={handleNotificationSubmit} className="space-y-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Email Notifications</h4>
                    <div className="space-y-3">
                      {Object.entries(notificationSettings.emailNotifications).map(([key, value]) => (
                        <div key={key} className="flex items-center">
                          <input
                            type="checkbox"
                            id={`email_${key}`}
                            checked={value}
                            onChange={(e) => setNotificationSettings(prev => ({
                              ...prev,
                              emailNotifications: {
                                ...prev.emailNotifications,
                                [key]: e.target.checked
                              }
                            }))}
                            className="mr-3"
                          />
                          <label htmlFor={`email_${key}`} className="text-sm text-gray-700 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">SMS Notifications</h4>
                    <div className="space-y-3">
                      {Object.entries(notificationSettings.smsNotifications).map(([key, value]) => (
                        <div key={key} className="flex items-center">
                          <input
                            type="checkbox"
                            id={`sms_${key}`}
                            checked={value}
                            onChange={(e) => setNotificationSettings(prev => ({
                              ...prev,
                              smsNotifications: {
                                ...prev.smsNotifications,
                                [key]: e.target.checked
                              }
                            }))}
                            className="mr-3"
                          />
                          <label htmlFor={`sms_${key}`} className="text-sm text-gray-700 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {loading ? (
                        <LoadingSpinner size="sm" color="white" className="mr-2" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save Notification Settings
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
